import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { chargePointActions } from "../store/actions/chargePoint.actions";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import validationHelper from "../utils/helpers/validation.helper";
import { Box } from "@mui/material";
import StartChargingTransactionProcessSteps from "../components/chargePoint/startChargingTransactionProcessSteps/StartChargingTransactionProcessSteps";
import LoaderComponent from "../components/LoaderComponent";
import alertService from "../services/alert/alert.service";
import { connectorStatusEnum } from "../enums/chargePoint/connectorStatusEnum";
import { chargePointStatusEnum } from "../enums/chargePoint/chargePointStatusEnum";
import queryParamsHelper from "../utils/helpers/queryParams.helper";
import { useSignalR } from "../hooks/useSignalR";
import { signalRKeysEnum } from "../enums/signalR/signalRKeysEnum";
import * as signalR from "@microsoft/signalr";
import chargingTransactionsErrorEnum from "../enums/errors/transactions/chargingTransactions/chargingTransactionsError.enum";

export const ChargePointDetailsPageComponent = ({ chargePointActions }) => {
  const navigate = useNavigate();
  const { companyId, chargePointId, partyId, uid, connectorNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [chargePointDetails, setChargePointDetails] = useState();
  const { isConnected, lastMessage, connection } = useSignalR();

  const stepFromQuery = queryParamsHelper.get(searchParams, "step", "0");
  const activeStep = parseInt(stepFromQuery, 10) || 0;

  useEffect(() => {
    const fetchChargePointDetails = async () => {
      setIsLoading(true);
      
      // Determine URL format
      const isOcpiFormat = partyId && uid && connectorNumber && !companyId && !chargePointId;
      const isGuidFormat = companyId && chargePointId && !partyId && !uid && !connectorNumber;
      
      let response;
      
      if (isGuidFormat) {
        // Existing GUID-based format - normal flow
        response = await chargePointActions.getChargePointDetails(chargePointId);
      } else if (isOcpiFormat) {
        // OCPI format - validate and redirect to GUID format
        const connectorNum = parseInt(connectorNumber, 10);
        if (isNaN(connectorNum)) {
          alertService.error("שגיאה", "מספר מחבר לא תקין", "אישור");
          navigate("/");
          setIsLoading(false);
          return;
        }
        
        // Fetch charge point details using OCPI identifiers
        response = await chargePointActions.getChargePointDetailsByExternalIdentifiers(partyId, uid, connectorNum);
        
        // If successful, redirect to GUID format URL
        if (validationHelper.validateHttpResponse(response) && response.payload) {
          const chargePoint = response.payload;
          // Redirect to GUID format - this will trigger a re-render with GUID params
          navigate(`/company/${chargePoint.companyId}/chargePoint/${chargePoint.id}`, { replace: true });
          setIsLoading(false);
          return; // Exit early - redirect will trigger new render with GUID params
        } else {
          // OCPI lookup failed - show error. Spec 000304 (Q7): a company that turned the module off says so.
          if (response?.errorCode === chargingTransactionsErrorEnum.CompanyNotActive) {
            alertService.error("התשלום בעמדה זו אינו זמין כרגע", "דף התשלום אינו פעיל בעמדה זו. ניתן לטעון באמצעות אפליקציית Advice EV.", "אישור");
          } else {
            alertService.error("עמדת טעינה לא נמצאה", "ניתן לסרוק את ברקוד העמדה או להזין כאן את פרטי העמדה", "אישור");
          }
          navigate("/");
          setIsLoading(false);
          return;
        }
      } else {
        // Invalid format
        alertService.error("שגיאה", "כתובת לא תקינה", "אישור");
        navigate("/");
        setIsLoading(false);
        return;
      }
      
      // Continue with normal flow (only for GUID format now)
      if (validationHelper.validateHttpResponse(response)) {
        const chargePoint = response.payload;
        handleChargePointDetails(chargePoint);
      } else {
        alertService.error("עמדת טעינה לא נמצאה", "ניתן לסרוק את ברקוד העמדה או להזין כאן את פרטי העמדה", "אישור");
        navigate("/");
      }
      setIsLoading(false);
    };

    fetchChargePointDetails();

    // Cleanup function to cancel active charge point events registration
    return () => {};
  }, [chargePointId, companyId, partyId, uid, connectorNumber, chargePointActions, navigate]);

  useEffect(() => {
    if (!chargePointId || !connection || !isConnected) return;

    const join = async () => {
      try {
        await connection.invoke("JoinChargePoint", chargePointId);
      } catch (e) {
        console.error("SignalR: failed to join charge point group", e);
      }
    };

    join();

    return () => {
      // Check if connection is still valid and connected before trying to leave
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        // Fire-and-forget: don't await, just try to leave and handle errors silently
        connection.invoke("LeaveChargePoint", chargePointId).catch(() => {
          // Connection is closing/closed - this is expected during navigation
          // Silently ignore the error to prevent console noise
        });
      }
    };
  }, [chargePointId, connection, isConnected]);

  useEffect(() => {
    if (lastMessage?.key !== signalRKeysEnum.RefreshChargePointStatus) return;
    const update = lastMessage.message;
    if (!update?.chargePointId || update.chargePointId !== chargePointId) return;

    const merged = {
      ...(chargePointDetails || {}),
      id: chargePointDetails?.id || chargePointId,
      chargePointStatus: update.chargePointStatus ?? chargePointDetails?.chargePointStatus,
      connectorStatus: update.connectorStatus ?? chargePointDetails?.connectorStatus,
      updateDate: update.updateDate ?? chargePointDetails?.updateDate,
    };

    if (chargePointDetails) {
      setChargePointDetails(merged);
    }

    // Apply existing navigation rules based on updated statuses
    if (merged.chargePointStatus && merged.connectorStatus) {
      handleChargePointDetails(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, chargePointId, chargePointDetails]);

  useEffect(() => {
    // Poll charge point details only while on "Start charging" step
    if (!chargePointId || activeStep !== 4) return;

    let isDisposed = false;

    const refreshChargePointDetails = async () => {
      const response = await chargePointActions.getChargePointDetails(chargePointId);
      if (isDisposed) return;

      if (validationHelper.validateHttpResponse(response) && response.payload) {
        handleChargePointDetails(response.payload);
      }
    };

    // Refresh immediately on entering step 4, then every 60s
    refreshChargePointDetails();
    const intervalId = setInterval(refreshChargePointDetails, 60_000);

    return () => {
      isDisposed = true;
      clearInterval(intervalId);
    };
  }, [activeStep, chargePointId, chargePointActions, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChargePointDetails = (chargePoint) => {
    // 1. check if the charge point connected
    if (chargePoint.chargePointStatus !== chargePointStatusEnum.Connected) {
      alertService.error("עמדת טעינה לא זמינה", `עמדת טעינה ${chargePoint.serialNumber} לא זמינה כעת. אנא נסה שנית מאוחר יותר`);
      navigate("/");
      return;
    }

    // 2. check if the connector busy
    if (
      chargePoint.connectorStatus === connectorStatusEnum.Charging ||
      chargePoint.connectorStatus === connectorStatusEnum.SuspendedEV ||
      chargePoint.connectorStatus === connectorStatusEnum.SuspendedEVSE
    ) {
      navigate(`/transactionLogin/chargePoint/${chargePoint.id}`);
      return;
    }

    setChargePointDetails(chargePoint);
  };

  if (isLoading) return <LoaderComponent />;

  if (chargePointDetails)
    return (
      <Box>
        <StartChargingTransactionProcessSteps chargePoint={chargePointDetails} />
      </Box>
    );
};

const mapStateToProps = (state) => ({
  chargePointReducer: state.chargePointReducer,
});

const mapDispatchToProps = (dispatch) => {
  return {
    chargePointActions: bindActionCreators(chargePointActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChargePointDetailsPageComponent);
